import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type {
  LearningAnalytics,
  PremiumPerformanceTrend,
} from '@coffeeplexity/shared';
import { LineChart } from 'react-native-chart-kit';

const { width } = Dimensions.get('window');

interface PredictiveModelingViewProps {
  analytics: LearningAnalytics;
  trends: PremiumPerformanceTrend[];
}

export const PredictiveModelingView: React.FC<PredictiveModelingViewProps> = ({
  analytics,
  trends,
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<
    '1month' | '3months' | '6months' | '1year'
  >('3months');
  const [selectedModel, setSelectedModel] = useState<
    'mastery' | 'engagement' | 'efficiency' | 'completion'
  >('mastery');

  const generatePredictiveData = (
    baseValue: number,
    growthRate: number,
    volatility: number,
    periods: number
  ) => {
    const data = [baseValue];
    let currentValue = baseValue;

    for (let i = 1; i <= periods; i++) {
      const trend = currentValue * (1 + growthRate / 100);
      const noise = (Math.random() - 0.5) * volatility;
      currentValue = Math.max(0, Math.min(100, trend + noise));
      data.push(currentValue);
    }

    return data;
  };

  const getTimeframeConfig = () => {
    switch (selectedTimeframe) {
      case '1month':
        return { periods: 4, label: 'weeks', growth: 2, volatility: 3 };
      case '3months':
        return { periods: 12, label: 'weeks', growth: 1.5, volatility: 4 };
      case '6months':
        return { periods: 6, label: 'months', growth: 3, volatility: 5 };
      case '1year':
        return { periods: 12, label: 'months', growth: 2, volatility: 6 };
      default:
        return { periods: 12, label: 'weeks', growth: 1.5, volatility: 4 };
    }
  };

  const renderMasteryPrediction = () => {
    const config = getTimeframeConfig();
    const currentMastery = analytics.overallProgress.masteryPercentage;
    const predictedData = generatePredictiveData(
      currentMastery,
      config.growth,
      config.volatility,
      config.periods
    );

    const chartData = {
      labels: Array.from({ length: config.periods + 1 }, (_, i) =>
        i === 0 ? 'Now' : `${i}${config.label === 'weeks' ? 'w' : 'm'}`
      ),
      datasets: [
        {
          data: predictedData,
          color: (opacity = 1) => `rgba(103, 80, 164, ${opacity})`,
          strokeWidth: 3,
        },
      ],
    };

    const finalPrediction = predictedData[predictedData.length - 1];
    const improvement = finalPrediction - currentMastery;

    return (
      <View style={styles.predictionCard}>
        <View style={styles.predictionHeader}>
          <Ionicons name='trending-up' size={24} color='#6750A4' />
          <View style={styles.predictionInfo}>
            <Text style={styles.predictionTitle}>Mastery Progression</Text>
            <Text style={styles.predictionSubtitle}>
              AI-powered learning trajectory forecast
            </Text>
          </View>
        </View>

        <View style={styles.predictionMetrics}>
          <View style={styles.currentMetric}>
            <Text style={styles.metricValue}>
              {Math.round(currentMastery)}%
            </Text>
            <Text style={styles.metricLabel}>Current Mastery</Text>
          </View>
          <Ionicons name='arrow-forward' size={20} color='#9CA3AF' />
          <View style={styles.predictedMetric}>
            <Text style={styles.metricValue}>
              {Math.round(finalPrediction)}%
            </Text>
            <Text style={styles.metricLabel}>
              Predicted ({selectedTimeframe})
            </Text>
          </View>
          <View style={styles.improvementMetric}>
            <Text
              style={[
                styles.improvementValue,
                { color: improvement > 0 ? '#10B981' : '#EF4444' },
              ]}
            >
              {improvement > 0 ? '+' : ''}
              {Math.round(improvement)}%
            </Text>
            <Text style={styles.metricLabel}>Improvement</Text>
          </View>
        </View>

        <LineChart
          data={chartData}
          width={width - 80}
          height={200}
          chartConfig={{
            backgroundColor: '#FFFFFF',
            backgroundGradientFrom: '#FFFFFF',
            backgroundGradientTo: '#FFFFFF',
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(103, 80, 164, ${opacity})`,
            labelColor: (opacity = 1) => `rgba(107, 114, 128, ${opacity})`,
            style: { borderRadius: 16 },
            fillShadowGradient: '#6750A4',
            fillShadowGradientOpacity: 0.1,
          }}
          bezier
          style={styles.chart}
        />

        <View style={styles.confidenceIndicator}>
          <View style={styles.confidenceHeader}>
            <Ionicons name='shield-checkmark' size={16} color='#10B981' />
            <Text style={styles.confidenceTitle}>Prediction Confidence</Text>
          </View>
          <View style={styles.confidenceBar}>
            <View style={[styles.confidenceFill, { width: '87%' }]} />
          </View>
          <Text style={styles.confidenceText}>
            87% confidence based on your learning patterns
          </Text>
        </View>

        <View style={styles.keyFactors}>
          <Text style={styles.factorsTitle}>Key Influencing Factors</Text>
          <View style={styles.factorsList}>
            <View style={styles.factorItem}>
              <Ionicons name='checkmark-circle' size={16} color='#10B981' />
              <Text style={styles.factorText}>
                Consistent daily practice (+15% impact)
              </Text>
            </View>
            <View style={styles.factorItem}>
              <Ionicons name='time' size={16} color='#F59E0B' />
              <Text style={styles.factorText}>
                Optimal learning schedule (+12% impact)
              </Text>
            </View>
            <View style={styles.factorItem}>
              <Ionicons name='people' size={16} color='#6750A4' />
              <Text style={styles.factorText}>
                Peer learning engagement (+8% impact)
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderEngagementPrediction = () => {
    const config = getTimeframeConfig();
    const currentEngagement = analytics.engagementMetrics.motivationLevel;
    const predictedData = generatePredictiveData(
      currentEngagement,
      config.growth * 0.5, // Engagement grows slower
      config.volatility * 1.5, // More volatile
      config.periods
    );

    const chartData = {
      labels: Array.from({ length: config.periods + 1 }, (_, i) =>
        i === 0 ? 'Now' : `${i}${config.label === 'weeks' ? 'w' : 'm'}`
      ),
      datasets: [
        {
          data: predictedData,
          color: (opacity = 1) => `rgba(16, 185, 129, ${opacity})`,
          strokeWidth: 3,
        },
      ],
    };

    return (
      <View style={styles.predictionCard}>
        <View style={styles.predictionHeader}>
          <Ionicons name='pulse' size={24} color='#10B981' />
          <View style={styles.predictionInfo}>
            <Text style={styles.predictionTitle}>Engagement Forecast</Text>
            <Text style={styles.predictionSubtitle}>
              Motivation and participation trends
            </Text>
          </View>
        </View>

        <LineChart
          data={chartData}
          width={width - 80}
          height={180}
          chartConfig={{
            backgroundColor: '#FFFFFF',
            backgroundGradientFrom: '#FFFFFF',
            backgroundGradientTo: '#FFFFFF',
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(16, 185, 129, ${opacity})`,
            labelColor: (opacity = 1) => `rgba(107, 114, 128, ${opacity})`,
            style: { borderRadius: 16 },
          }}
          bezier
          style={styles.chart}
        />

        <View style={styles.engagementInsights}>
          <View style={styles.insightCard}>
            <Ionicons name='trending-up' size={20} color='#10B981' />
            <View style={styles.insightContent}>
              <Text style={styles.insightTitle}>Peak Engagement Period</Text>
              <Text style={styles.insightText}>
                Weeks 4-8 show highest predicted engagement
              </Text>
            </View>
          </View>
          <View style={styles.insightCard}>
            <Ionicons name='warning' size={20} color='#F59E0B' />
            <View style={styles.insightContent}>
              <Text style={styles.insightTitle}>Attention Point</Text>
              <Text style={styles.insightText}>
                Monitor engagement around week 10-11
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderEfficiencyPrediction = () => {
    const config = getTimeframeConfig();
    const currentEfficiency = analytics.overallProgress.efficiencyScore;
    const predictedData = generatePredictiveData(
      currentEfficiency,
      config.growth * 0.8,
      config.volatility * 0.8,
      config.periods
    );

    const chartData = {
      labels: Array.from({ length: config.periods + 1 }, (_, i) =>
        i === 0 ? 'Now' : `${i}${config.label === 'weeks' ? 'w' : 'm'}`
      ),
      datasets: [
        {
          data: predictedData,
          color: (opacity = 1) => `rgba(245, 158, 11, ${opacity})`,
          strokeWidth: 3,
        },
      ],
    };

    return (
      <View style={styles.predictionCard}>
        <View style={styles.predictionHeader}>
          <Ionicons name='speedometer' size={24} color='#F59E0B' />
          <View style={styles.predictionInfo}>
            <Text style={styles.predictionTitle}>Efficiency Optimization</Text>
            <Text style={styles.predictionSubtitle}>
              Learning effectiveness trajectory
            </Text>
          </View>
        </View>

        <LineChart
          data={chartData}
          width={width - 80}
          height={180}
          chartConfig={{
            backgroundColor: '#FFFFFF',
            backgroundGradientFrom: '#FFFFFF',
            backgroundGradientTo: '#FFFFFF',
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(245, 158, 11, ${opacity})`,
            labelColor: (opacity = 1) => `rgba(107, 114, 128, ${opacity})`,
            style: { borderRadius: 16 },
          }}
          bezier
          style={styles.chart}
        />

        <View style={styles.efficiencyBreakdown}>
          <Text style={styles.breakdownTitle}>Efficiency Drivers</Text>
          <View style={styles.driversList}>
            <View style={styles.driverItem}>
              <View style={styles.driverBar}>
                <View
                  style={[
                    styles.driverFill,
                    { width: '85%', backgroundColor: '#10B981' },
                  ]}
                />
              </View>
              <Text style={styles.driverLabel}>Study Habits (85%)</Text>
            </View>
            <View style={styles.driverItem}>
              <View style={styles.driverBar}>
                <View
                  style={[
                    styles.driverFill,
                    { width: '78%', backgroundColor: '#F59E0B' },
                  ]}
                />
              </View>
              <Text style={styles.driverLabel}>Time Management (78%)</Text>
            </View>
            <View style={styles.driverItem}>
              <View style={styles.driverBar}>
                <View
                  style={[
                    styles.driverFill,
                    { width: '72%', backgroundColor: '#6750A4' },
                  ]}
                />
              </View>
              <Text style={styles.driverLabel}>Focus Quality (72%)</Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderCompletionPrediction = () => {
    const config = getTimeframeConfig();
    const currentCompletion = analytics.engagementMetrics.completionRate;
    const predictedData = generatePredictiveData(
      currentCompletion,
      config.growth * 0.6,
      config.volatility * 1.2,
      config.periods
    );

    const chartData = {
      labels: Array.from({ length: config.periods + 1 }, (_, i) =>
        i === 0 ? 'Now' : `${i}${config.label === 'weeks' ? 'w' : 'm'}`
      ),
      datasets: [
        {
          data: predictedData,
          color: (opacity = 1) => `rgba(139, 92, 246, ${opacity})`,
          strokeWidth: 3,
        },
      ],
    };

    return (
      <View style={styles.predictionCard}>
        <View style={styles.predictionHeader}>
          <Ionicons name='checkmark-circle' size={24} color='#8B5CF6' />
          <View style={styles.predictionInfo}>
            <Text style={styles.predictionTitle}>Completion Forecast</Text>
            <Text style={styles.predictionSubtitle}>
              Task and goal completion trends
            </Text>
          </View>
        </View>

        <LineChart
          data={chartData}
          width={width - 80}
          height={180}
          chartConfig={{
            backgroundColor: '#FFFFFF',
            backgroundGradientFrom: '#FFFFFF',
            backgroundGradientTo: '#FFFFFF',
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(139, 92, 246, ${opacity})`,
            labelColor: (opacity = 1) => `rgba(107, 114, 128, ${opacity})`,
            style: { borderRadius: 16 },
          }}
          bezier
          style={styles.chart}
        />

        <View style={styles.completionMilestones}>
          <Text style={styles.milestonesTitle}>Predicted Milestones</Text>
          <View style={styles.milestonesList}>
            <View style={styles.milestoneItem}>
              <View style={styles.milestoneIcon}>
                <Ionicons name='flag' size={16} color='#6750A4' />
              </View>
              <View style={styles.milestoneContent}>
                <Text style={styles.milestoneTitle}>Intermediate Level</Text>
                <Text style={styles.milestoneDate}>
                  Expected:{' '}
                  {new Date(
                    Date.now() + 21 * 24 * 60 * 60 * 1000
                  ).toLocaleDateString()}
                </Text>
              </View>
              <Text style={styles.milestoneProbability}>85%</Text>
            </View>
            <View style={styles.milestoneItem}>
              <View style={styles.milestoneIcon}>
                <Ionicons name='trophy' size={16} color='#F59E0B' />
              </View>
              <View style={styles.milestoneContent}>
                <Text style={styles.milestoneTitle}>Advanced Techniques</Text>
                <Text style={styles.milestoneDate}>
                  Expected:{' '}
                  {new Date(
                    Date.now() + 45 * 24 * 60 * 60 * 1000
                  ).toLocaleDateString()}
                </Text>
              </View>
              <Text style={styles.milestoneProbability}>72%</Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderContent = () => {
    switch (selectedModel) {
      case 'mastery':
        return renderMasteryPrediction();
      case 'engagement':
        return renderEngagementPrediction();
      case 'efficiency':
        return renderEfficiencyPrediction();
      case 'completion':
        return renderCompletionPrediction();
      default:
        return renderMasteryPrediction();
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Predictive Analytics</Text>
        <Text style={styles.subtitle}>
          AI-powered forecasting based on your learning patterns
        </Text>
      </View>

      <View style={styles.controls}>
        <View style={styles.modelSelector}>
          <Text style={styles.selectorLabel}>Prediction Model</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.selectorContent}
          >
            {[
              { id: 'mastery', title: 'Mastery', icon: 'trending-up' },
              { id: 'engagement', title: 'Engagement', icon: 'pulse' },
              { id: 'efficiency', title: 'Efficiency', icon: 'speedometer' },
              {
                id: 'completion',
                title: 'Completion',
                icon: 'checkmark-circle',
              },
            ].map(model => (
              <TouchableOpacity
                key={model.id}
                style={[
                  styles.selectorButton,
                  selectedModel === model.id && styles.activeSelectorButton,
                ]}
                onPress={() => setSelectedModel(model.id as any)}
              >
                <Ionicons
                  name={model.icon as any}
                  size={16}
                  color={selectedModel === model.id ? '#FFFFFF' : '#6750A4'}
                />
                <Text
                  style={[
                    styles.selectorButtonText,
                    selectedModel === model.id &&
                      styles.activeSelectorButtonText,
                  ]}
                >
                  {model.title}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        <View style={styles.timeframeSelector}>
          <Text style={styles.selectorLabel}>Forecast Period</Text>
          <View style={styles.timeframeButtons}>
            {[
              { id: '1month', title: '1M' },
              { id: '3months', title: '3M' },
              { id: '6months', title: '6M' },
              { id: '1year', title: '1Y' },
            ].map(timeframe => (
              <TouchableOpacity
                key={timeframe.id}
                style={[
                  styles.timeframeButton,
                  selectedTimeframe === timeframe.id &&
                    styles.activeTimeframeButton,
                ]}
                onPress={() => setSelectedTimeframe(timeframe.id as any)}
              >
                <Text
                  style={[
                    styles.timeframeButtonText,
                    selectedTimeframe === timeframe.id &&
                      styles.activeTimeframeButtonText,
                  ]}
                >
                  {timeframe.title}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {renderContent()}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  controls: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 16,
  },
  modelSelector: {
    marginBottom: 16,
  },
  selectorLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
    paddingHorizontal: 20,
  },
  selectorContent: {
    paddingHorizontal: 20,
    gap: 8,
  },
  selectorButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    gap: 6,
  },
  activeSelectorButton: {
    backgroundColor: '#6750A4',
    borderColor: '#6750A4',
  },
  selectorButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6750A4',
  },
  activeSelectorButtonText: {
    color: '#FFFFFF',
  },
  timeframeSelector: {
    paddingHorizontal: 20,
  },
  timeframeButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  timeframeButton: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
  },
  activeTimeframeButton: {
    backgroundColor: '#6750A4',
  },
  timeframeButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6750A4',
  },
  activeTimeframeButtonText: {
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  predictionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  predictionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 12,
  },
  predictionInfo: {
    flex: 1,
  },
  predictionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  predictionSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  predictionMetrics: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    marginBottom: 20,
  },
  currentMetric: {
    alignItems: 'center',
  },
  predictedMetric: {
    alignItems: 'center',
  },
  improvementMetric: {
    alignItems: 'center',
  },
  metricValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
  },
  metricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
    textAlign: 'center',
  },
  improvementValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  chart: {
    borderRadius: 16,
    marginVertical: 16,
  },
  confidenceIndicator: {
    backgroundColor: '#F0FDF4',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  confidenceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 6,
  },
  confidenceTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#166534',
  },
  confidenceBar: {
    height: 6,
    backgroundColor: '#BBF7D0',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 6,
  },
  confidenceFill: {
    height: '100%',
    backgroundColor: '#16A34A',
    borderRadius: 3,
  },
  confidenceText: {
    fontSize: 12,
    color: '#166534',
  },
  keyFactors: {
    marginTop: 8,
  },
  factorsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  factorsList: {
    gap: 8,
  },
  factorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  factorText: {
    fontSize: 14,
    color: '#4B5563',
    flex: 1,
  },
  engagementInsights: {
    marginTop: 16,
    gap: 12,
  },
  insightCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    gap: 12,
  },
  insightContent: {
    flex: 1,
  },
  insightTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  insightText: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
  efficiencyBreakdown: {
    marginTop: 16,
  },
  breakdownTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  driversList: {
    gap: 12,
  },
  driverItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  driverBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    overflow: 'hidden',
  },
  driverFill: {
    height: '100%',
    borderRadius: 4,
  },
  driverLabel: {
    fontSize: 12,
    color: '#6B7280',
    minWidth: 120,
  },
  completionMilestones: {
    marginTop: 16,
  },
  milestonesTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  milestonesList: {
    gap: 12,
  },
  milestoneItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    gap: 12,
  },
  milestoneIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#EDE7F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  milestoneContent: {
    flex: 1,
  },
  milestoneTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  milestoneDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  milestoneProbability: {
    fontSize: 14,
    fontWeight: '600',
    color: '#8B5CF6',
  },
});
